import classnames from 'classnames';
import * as _ from 'lodash';
import type { FunctionComponent, ReactNode } from 'react';

import Accordion from '../Accordion';
import { Badge } from '../Badge';
import Flex from '../Flex';

import Loading from './DetailsPanelLoading';

import styles from './DetailsPanel.module.less';

type Props = {
  /**
   * A unique ID for the accordion component.
   */
  id: string;
  /**
   * A label for the click target of the accordion.
   */
  target: string;
  /**
   * Target can optionally display a small count beside the target.
   */
  targetCount?: number;
  /**
   * Optional button element to display in the target area.
   */
  targetButton?: ReactNode;
  /**
   * If the accordion is initially open
   */
  isInitiallyOpen?: boolean;
  /**
   * If the loading UI should be displayed.
   */
  isLoading?: boolean;
  /**
   * Optionally you can decide how many "ghost rows" are displayed while the panel is loading. (@default 6)
   */
  loadingRowCount?: number;
  /**
   * If the body should bleed to the panel's horizontal edges, for full-width
   * children like banners. The header keeps its inset.
   */
  isUnpadded?: boolean;
};

/**
 * DetailsPanel is an accordion panel which displays a list of rows,
 * where each row contains a left-aligned "label" and a right-aligned
 * "content", which may contain any arbitrary content.
 *
 * @example
 * <DetailsPanel
 *   id="vuln-details"
 *   target="Details"
 *   isLoading={asyncStatus.isLoading}
 * >
 *   <div>Some details! Any `children` will work here!</div>
 * </DetailsPanel>
 */
const DetailsPanel: FunctionComponent<Props> = ({
  id,
  target,
  children,
  isInitiallyOpen = true,
  isLoading,
  loadingRowCount = 6,
  targetCount,
  targetButton,
  isUnpadded,
}) => (
  <div className={styles.panel}>
    <Accordion id={id} isInitiallyOpen={isInitiallyOpen}>
      <Accordion.Target>
        <Flex justify="space-between" align="center" className="width-full">
          <span>
            {target}{!_.isNil(targetCount) && (
              <span className="margin-left-xs">
                <Badge color="light-gray">
                  {targetCount}
                </Badge>
              </span>
            )}
          </span>
          {targetButton}
        </Flex>
      </Accordion.Target>
      <Accordion.Body>
        <div className={classnames(styles.body, { [styles.bodyUnpadded]: isUnpadded })}>
          {isLoading ? <Loading rowCount={loadingRowCount} /> : children}
        </div>
      </Accordion.Body>
    </Accordion>
  </div>
);

export { default as DetailsPanelLink } from './DetailsPanelLink';
export { default as DetailsPanelList } from './DetailsPanelList';

export default DetailsPanel;
